<template>
  <div>
    <ValidationObserver v-slot="{ handleSubmit, reset }">
      <div class="mb-6">
        <survey-header />
      </div>

      <div v-if="loadingSurvey || submittingSurvey">
        <Loading class="block h-32 mx-auto w-32" />
      </div>
      <div v-else>
        <div
          v-if="!hasSurvey"
          class="md:px-16"
        >
          <div class="bg-red-200 text-red-800 mt-2 p-4">
            Failed to load survey
          </div>
        </div>

        <form
          ref="survey"
          aria-live="assertive"
        >
          <transition-group
            :enter-active-class="enterActiveClass"
            :leave-active-class="leaveActiveClass"
            :enter-class="enterClass"
            :enter-to-class="enterToClass"
            :leave-class="leaveClass"
            :leave-to-class="leaveToClass"
          >
            <template v-for="(page, pageIndex) in shownPages">
              <div
                v-if="page.id === currentPage"
                :key="pageIndex"
                class="page-container md:px-16"
              >
                <div class="flex justify-between">
                  <div
                    v-if="page.name"
                    class="text-lg md:text-2xl text-red-600 font-extrabold uppercase"
                  >
                    {{ page.name }}
                  </div>
                </div>

                <template v-if="pageIndex === 0">
                  <div
                    v-if="survey.introduction_image"
                    class="mb-8"
                  >
                    <img
                      :src="survey.introduction_image"
                      alt=""
                    >
                  </div>

                  <div
                    v-if="survey.summary_text"
                    class="mb-8 wysiwyg"
                    v-html="$sanitize(survey.summary_text)"
                  />
                </template>

                <template v-for="item in page.items">
                  <div
                    v-if="item.show"
                    :key="item.id"
                    class="item-container mb-8"
                  >
                    <component
                      :is="itemComponent(item)"
                      :item="item"
                    />
                  </div>
                </template>
              </div>
            </template>
          </transition-group>
        </form>
        <div class="md:px-16 flex justify-between items-center">
          <div class="w-full">
            <survey-nav :handle-submit="handleSubmit" />
          </div>
          <div class="hidden md:block">
            <survey-percentage
              :percentage="pagePercent"
              :large="true"
            />
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>

import Loading from '@/components/Loading'
import SurveyNav from '@/components/survey/SurveyNav'
import SurveyHeader from '@/components/survey/SurveyHeader'
import SurveyPercentage from '@/components/survey/SurveyPercentage'

import ItemText from '@/components/survey/item-text'
import ItemLongText from '@/components/survey/item-long-text'
import ItemEmail from '@/components/survey/item-email'
import ItemLabel from '@/components/survey/item-label'
import ItemNumeric from '@/components/survey/item-numeric'
import ItemDate from '@/components/survey/item-date'
import ItemTime from '@/components/survey/item-time'
import ItemDuration from '@/components/survey/item-duration'
import ItemPostcode from '@/components/survey/item-postcode'
import ItemSingleCheckbox from '@/components/survey/item-single-checkbox'
import ItemCheckboxes from '@/components/survey/item-checkboxes'
import ItemDropDown from '@/components/survey/item-drop-down'
import ItemMultipleChoice from '@/components/survey/item-multiple-choice'
import ItemMultiChoiceGrid from '@/components/survey/item-multi-choice-grid'
import ItemLinearScale from '@/components/survey/item-linear-scale'
import ItemThermometer from '@/components/survey/item-thermometer'
import ItemWellbeingScale from '@/components/survey/item-wellbeing-scale'
import ItemBeneficiary from '@/components/survey/item-beneficiary'

import { createNamespacedHelpers } from 'vuex'
const { mapGetters } = createNamespacedHelpers('survey')

export default {
  components: {
    Loading,
    SurveyNav,
    SurveyHeader,
    SurveyPercentage,
    ItemText,
    ItemLongText,
    ItemEmail,
    ItemLabel,
    ItemNumeric,
    ItemDate,
    ItemTime,
    ItemDuration,
    ItemPostcode,
    ItemSingleCheckbox,
    ItemCheckboxes,
    ItemDropDown,
    ItemMultipleChoice,
    ItemMultiChoiceGrid,
    ItemLinearScale,
    ItemThermometer,
    ItemWellbeingScale,
    ItemBeneficiary
  },
  computed: {
    ...mapGetters([
      'loadingSurvey',
      'submittingSurvey',
      'survey',
      'surveyHash',
      'shownPages',
      'pagePercent',
      'itemTypes',
      'currentPage',
      'directions',
      'currentDirection'
    ]),
    enterActiveClass () {
      return 'transition-all'
    },
    leaveActiveClass () {
      return 'absolute transition-all'
    },
    enterClass () {
      if (this.currentDirection === this.directions.PREV) {
        return 'opacity-0 scale-70 -translate-x-100'
      } else {
        return 'opacity-0 scale-70 translate-x-100'
      }
    },
    enterToClass () {
      return 'opacity-100 scale-100 translate-x-0'
    },
    leaveClass () {
      return 'opacity-100 scale-100 translate-x-0'
    },
    leaveToClass () {
      if (this.currentDirection === this.directions.PREV) {
        return 'opacity-0 scale-70 translate-x-100'
      } else {
        return 'opacity-0 scale-70 -translate-x-100'
      }
    },
    hasSurvey () {
      return this.survey !== null
    }
  },
  mounted () {
    window.addEventListener('beforeunload', this.handleLeave)
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.handleLeave)
  },
  methods: {
    handleLeave (event) {
      event.returnValue = 'You have unfinished changes!'
    },
    itemComponent ({ type }) {
      return `item-${type.replace(/_/g, '-')}`
    }
  }
}
</script>
